import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

// Reuses the same Web3forms access key + fire-and-forget pattern as
// MessageFeedback.tsx — no new tables, no SQL. Subject distinguishes these
// in the inbox from per-message response feedback.
const WEB3FORMS_ACCESS_KEY = "eabca370-87d5-4393-aab8-2f6e3778c2dc";

interface GetHelpProps {
  embedded?: boolean;
}

export default function GetHelp({ embedded = false }: GetHelpProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [context, setContext] = useState("");
  const [replyEmail, setReplyEmail] = useState(user?.email || "");
  const [replyEmailEdited, setReplyEmailEdited] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!replyEmailEdited && user?.email) {
      setReplyEmail(user.email);
    }
  }, [user?.email, replyEmailEdited]);

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed || sending) return;
    setSending(true);

    const trimmedReplyEmail = replyEmail.trim();

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: "Kin Help Request",
          // Her account email so she never has to type it herself. This also
          // sets the reply-to target on the delivered email.
          email: trimmedReplyEmail || user?.email || "unknown",
          from_name: trimmedReplyEmail || user?.email || "A Kin user",
          message: trimmed,
          what_were_you_doing: context.trim() || null,
          user_id: user?.id || null,
          user_email: trimmedReplyEmail || user?.email || null,
          timestamp: new Date().toISOString(),
        }),
      });

      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) throw new Error("send failed");

      // Success — clear the form and confirm.
      setMessage("");
      setContext("");
      setOpen(false);
      toast("Sent 💌 We'll get back to you soon.", {
        style: {
          background: "#FFFFFF",
          border: "1.5px solid #5050C8",
          fontFamily: "'DM Serif Display', serif",
          color: "#1C1A3E",
        },
        duration: 3000,
      });
    } catch {
      // Never eat her message — leave the text in place so she can retry.
      toast.error("Hmm, that didn't go through. Mind trying once more? 💜", {
        duration: 4000,
      });
    } finally {
      setSending(false);
    }
  };

  const formFields = (
    <div style={embedded ? undefined : { padding: "0 18px 18px 18px" }}>
            <label
              className="font-body"
              style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#54525C", marginBottom: 6 }}
            >
              What's going on?
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell us what's happening…"
              rows={4}
              aria-label="Your message"
              style={{
                width: "100%",
                padding: "10px 12px",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 14,
                color: "#1C1A3E",
                backgroundColor: "#F0F0FF",
                border: "1.5px solid #C8C8F4",
                borderRadius: 12,
                resize: "none",
                outline: "none",
              }}
            />

            <label
              className="font-body"
              style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#54525C", margin: "12px 0 6px" }}
            >
              What were you doing? <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <input
              type="text"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="e.g. trying to save a grocery list"
              aria-label="What were you doing"
              style={{
                width: "100%",
                padding: "10px 12px",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 14,
                color: "#1C1A3E",
                backgroundColor: "#F0F0FF",
                border: "1.5px solid #C8C8F4",
                borderRadius: 12,
                outline: "none",
              }}
            />

            <label
              className="font-body"
              style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#54525C", margin: "12px 0 6px" }}
            >
              We'll reply to:
            </label>
            <input
              type="email"
              value={replyEmail}
              onChange={(e) => {
                setReplyEmail(e.target.value);
                setReplyEmailEdited(true);
              }}
              placeholder="your account email"
              aria-label="Reply email"
              style={{
                width: "100%",
                padding: "10px 12px",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 14,
                color: "#1C1A3E",
                backgroundColor: "#F0F0FF",
                border: "1.5px solid #C8C8F4",
                borderRadius: 12,
                outline: "none",
              }}
            />

            <button
              onClick={handleSend}
              disabled={!message.trim() || sending}
              className="w-full font-body font-semibold text-sm text-white transition-all hover:opacity-90 disabled:opacity-50"
              style={{
                marginTop: 14,
                height: 44,
                borderRadius: 12,
                backgroundColor: "#5050C8",
                border: "none",
                cursor: !message.trim() || sending ? "default" : "pointer",
              }}
            >
              {sending ? "Sending…" : "Send"}
            </button>
    </div>
  );

  if (embedded) return formFields;

  return (
    <div>
      <h2
        className="font-body text-xs font-semibold uppercase tracking-widest mb-3"
        style={{ color: "#54525C" }}
      >
        Get help
      </h2>
      <div
        className="bg-white"
        style={{ border: "1.5px solid #C8C8F4", borderRadius: 16, overflow: "hidden" }}
      >
        <button
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: 18,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          <div className="min-w-0">
            <p className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>
              Get help
            </p>
            <p className="font-body text-xs mt-0.5" style={{ color: "#54525C", lineHeight: 1.5 }}>
              Something broken? Confusing? Tell us — a real human reads these 💕
            </p>
          </div>
          <ChevronDown
            size={18}
            color="#5050C8"
            style={{
              flexShrink: 0,
              transition: "transform 200ms ease",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        </button>

        <div
          style={{
            maxHeight: open ? 520 : 0,
            overflow: "hidden",
            transition: "max-height 220ms ease",
          }}
        >
          {formFields}
        </div>
      </div>
    </div>
  );
}
